/**
 * Frequency derivation — HKDF-SHA256 over a public key, formatted as
 *   @XXXX.XXXX.XXXX  (12 base36 chars in 3 groups of 4)
 *
 * Cross-platform contract: the SDK and any other client deriving a
 * frequency from the same key bytes MUST produce the same string. The
 * info string and bit-allocation are pinned here.
 */

const INFO = new TextEncoder().encode('flock-frequency-v1');
const HKDF_OUT_BYTES = 12;

export async function deriveFrequency(keyBytes: Uint8Array): Promise<string> {
  const salt = new Uint8Array(32);
  const baseKey = await crypto.subtle.importKey(
    'raw',
    keyBytes as unknown as ArrayBuffer,
    'HKDF',
    false,
    ['deriveBits'],
  );
  const out = new Uint8Array(
    await crypto.subtle.deriveBits(
      { name: 'HKDF', hash: 'SHA-256', salt, info: INFO },
      baseKey,
      HKDF_OUT_BYTES * 8,
    ),
  );

  return formatFrequency(out);
}

export function formatFrequency(bytes: Uint8Array): string {
  if (bytes.length < 12) {
    throw new Error(`frequency requires 12 bytes, got ${bytes.length}`);
  }
  const a = chunk(bytes, 0);
  const b = chunk(bytes, 4);
  const c = chunk(bytes, 8);
  return `@${a}.${b}.${c}`;
}

function chunk(bytes: Uint8Array, offset: number): string {
  const b0 = bytes[offset] ?? 0;
  const b1 = bytes[offset + 1] ?? 0;
  const b2 = bytes[offset + 2] ?? 0;
  const b3 = bytes[offset + 3] ?? 0;
  const value = ((b0 << 24) | (b1 << 16) | (b2 << 8) | b3) >>> 0;
  const mod = value % 36 ** 4;
  return mod.toString(36).padStart(4, '0');
}

// Mirror of server/src/validation.ts FREQUENCY_REGEX. Kept in sync manually.
export const FREQUENCY_REGEX = /^@[0-9a-z]{4}\.[0-9a-z]{4}\.[0-9a-z]{4}$/;

export function isValidFrequency(value: string): boolean {
  return FREQUENCY_REGEX.test(value);
}
